import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import {
  GoogleMap,
  GroundOverlay,
  InfoWindow,
  Marker,
  Polyline,
  useJsApiLoader,
} from "@react-google-maps/api";
import {
  getChallengeById,
  getPortalParticipantsByProductId,
} from "../../globalService/api.service";
import { Challenge, PortalParticipant } from "../../globalService/api.model";
import { RefreshCw, Search, Target } from "lucide-react";
import "./ChallengePortal.css";

const googleMapsApiKey = "AIzaSyAFgWyTzNPhooiqiZJIr-Judt3Wo14w6GE";

type SortKey = "name" | "progress" | "distance";
type LatLngLiteral = { lat: number; lng: number };
type BoundsLiteral = { north: number; south: number; east: number; west: number };

const safeNum = (n: any, fallback = 0) =>
  Number.isFinite(Number(n)) ? Number(n) : fallback;

const formatKm = (km: number) => {
  if (!Number.isFinite(km)) return "–";
  if (km < 1) return `${(km * 1000).toFixed(0)} m`;
  return `${km.toFixed(1)} km`;
};

const formatPct = (pct: number) => {
  const p = Math.max(0, Math.min(100, safeNum(pct, 0)));
  return `${p.toFixed(0)}%`;
};

const cleanUsername = (name: string) =>
  name
    .toLowerCase()
    .replace(/\s+/g, "")
    .replace(/\./g, "")
    .replace(/[^a-z0-9]/g, "");

const haversineMeters = (a: LatLngLiteral, b: LatLngLiteral) => {
  const R = 6371000;
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);
  const sin1 = Math.sin(dLat / 2);
  const sin2 = Math.sin(dLng / 2);
  const h = sin1 * sin1 + Math.cos(lat1) * Math.cos(lat2) * sin2 * sin2;
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(h)));
};

const parseMaybeJson = (value: any) => {
  if (!value || value === "null") return null;
  if (typeof value !== "string") return value;
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
};

const parseBoundsLiteral = (bounds?: any): BoundsLiteral | null => {
  const b = parseMaybeJson(bounds);
  if (!b) return null;

  // Format already used by Google GroundOverlay.
  if (
    Number.isFinite(Number(b.north)) &&
    Number.isFinite(Number(b.south)) &&
    Number.isFinite(Number(b.east)) &&
    Number.isFinite(Number(b.west))
  ) {
    return {
      north: Number(b.north),
      south: Number(b.south),
      east: Number(b.east),
      west: Number(b.west),
    };
  }

  // Common SW/NE shape fallback.
  const sw = b.southWest || b.sw || b.SW;
  const ne = b.northEast || b.ne || b.NE;
  if (sw && ne) {
    const south = safeNum(sw.lat ?? sw.latitude, NaN);
    const west = safeNum(sw.lng ?? sw.longitude, NaN);
    const north = safeNum(ne.lat ?? ne.latitude, NaN);
    const east = safeNum(ne.lng ?? ne.longitude, NaN);
    if ([south, west, north, east].every((x) => Number.isFinite(x))) {
      return { south, west, north, east };
    }
  }

  return null;
};

const getChallengeName = (challenge: any, productId?: string) =>
  challenge?.name || challenge?.title || challenge?.post_title || `Challenge Portal #${productId}`;

export default function ChallengePortal() {
  const params = useParams();
  const brandId = params.brandId as string | undefined;
  const productId = params.productId as string | undefined;

  const [challenge, setChallenge] = useState<Challenge | null>(null);
  const [participants, setParticipants] = useState<PortalParticipant[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [query, setQuery] = useState("");
  const [sortKey, setSortKey] = useState<SortKey>("progress");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");
  const [focusedUserId, setFocusedUserId] = useState<number | null>(null);

  const [infoWindow, setInfoWindow] = useState<{
    position: LatLngLiteral;
    title: string;
    sub?: string;
  } | null>(null);

  const mapRef = useRef<any>(null);
  const mapCardRef = useRef<HTMLDivElement | null>(null);

  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey,
  });

  useEffect(() => {
    if (brandId) localStorage.setItem("brandId", brandId);
  }, [brandId]);

  const hydrateParticipants = (list: PortalParticipant[]) => {
    return (list || []).map((p: any, index: number) => {
      const userId =
        safeNum(p.userId, NaN) ||
        safeNum(p.userid, NaN) ||
        safeNum(p.user_id, NaN) ||
        safeNum(p.ID, NaN) ||
        index + 1;

      const name = (
        p.name ||
        p.username ||
        p.userName ||
        p.displayName ||
        p.display_name ||
        p.user_login ||
        `User ${userId}`
      )
        .toString()
        .trim();

      return {
        ...p,
        userId,
        name,
        percentage: safeNum(p.percentage ?? p.progress ?? p.percent, 0),
        distanceKm: safeNum(p.distanceKm ?? p.distance_km ?? p.distance, 0),
      };
    });
  };

  const loadAll = async () => {
    if (!productId) return;

    setLoading(true);
    setError(null);

    try {
      const [challengeData, pplData] = await Promise.all([
        getChallengeById(productId),
        getPortalParticipantsByProductId(productId, brandId),
      ]);

      if (challengeData && challengeData.length) {
        const first = challengeData[0] as any;
        setChallenge({
          ...first,
          pathCoordinates: parseMaybeJson(first.pathCoordinates) || first.pathCoordinates,
        } as Challenge);
      } else {
        setChallenge(null);
      }

      setParticipants(hydrateParticipants(Array.isArray(pplData) ? pplData : []));
    } catch (e: any) {
      setError(e?.message || "Failed to load portal data.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brandId, productId]);

  const buildPolyline = (pathCoordinates: any): LatLngLiteral[] => {
    const coords = parseMaybeJson(pathCoordinates) || pathCoordinates;
    if (!Array.isArray(coords)) return [];

    return coords
      .map((p: any) => ({
        lat: safeNum(p.lat ?? p.latitude, NaN),
        lng: safeNum(p.lng ?? p.longitude, NaN),
      }))
      .filter((p: LatLngLiteral) => Number.isFinite(p.lat) && Number.isFinite(p.lng));
  };

  const routePath = useMemo(
    () => buildPolyline((challenge as any)?.pathCoordinates),
    [challenge]
  );

  const routeMetrics = useMemo(() => {
    if (!routePath || routePath.length < 2) {
      return { totalMeters: 0, cumMeters: [0] as number[] };
    }

    const cum: number[] = [0];
    let total = 0;

    for (let i = 1; i < routePath.length; i++) {
      total += haversineMeters(routePath[i - 1], routePath[i]);
      cum.push(total);
    }

    return { totalMeters: total, cumMeters: cum };
  }, [routePath]);

  const totalDistanceKm = useMemo(() => {
    const challengeAny = challenge as any;
    const d = safeNum(
      challengeAny?.totalDistanceKm ?? challengeAny?.distanceKm ?? challengeAny?.distance,
      NaN
    );
    if (Number.isFinite(d)) return d;
    if (routeMetrics.totalMeters) return routeMetrics.totalMeters / 1000;
    return Math.max(...participants.map((p) => safeNum(p.distanceKm, 0)), 0);
  }, [challenge, participants, routeMetrics.totalMeters]);

  const getPositionByPercentage = (pct: number): LatLngLiteral | null => {
    if (!routePath.length) return null;

    const p = Math.max(0, Math.min(100, safeNum(pct, 0)));
    const routeTotal = routeMetrics.totalMeters || safeNum(totalDistanceKm, 0) * 1000;

    if (!routeTotal) return routePath[0] || null;

    const target = (p / 100) * routeTotal;
    const cum = routeMetrics.cumMeters;

    let idx = 0;
    while (idx < cum.length - 1 && cum[idx + 1] < target) idx++;

    const a = routePath[idx];
    const b = routePath[Math.min(idx + 1, routePath.length - 1)];
    const segStart = cum[idx] ?? 0;
    const segEnd = cum[Math.min(idx + 1, cum.length - 1)] ?? segStart;
    const segLen = Math.max(1e-6, segEnd - segStart);
    const t = Math.max(0, Math.min(1, (target - segStart) / segLen));

    return {
      lat: a.lat + (b.lat - a.lat) * t,
      lng: a.lng + (b.lng - a.lng) * t,
    };
  };

  const fitToRouteOrBounds = () => {
    if (!mapRef.current || !(window as any).google) return;

    const overlayBounds = parseBoundsLiteral((challenge as any)?.bounds);
    const maps = (window as any).google.maps;

    if (overlayBounds) {
      const b = new maps.LatLngBounds(
        { lat: overlayBounds.south, lng: overlayBounds.west },
        { lat: overlayBounds.north, lng: overlayBounds.east }
      );

      mapRef.current.fitBounds(b);
      return;
    }

    if (!routePath.length) return;

    const bounds = new maps.LatLngBounds();
    routePath.forEach((p) => bounds.extend(p));
    mapRef.current.fitBounds(bounds);
  };

  useEffect(() => {
    if (!isLoaded || !mapRef.current) return;

    const timer = window.setTimeout(() => {
      fitToRouteOrBounds();
    }, 250);

    return () => window.clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoaded, challenge, routePath.length]);

  const focusParticipant = (p: PortalParticipant) => {
    const uid = safeNum((p as any).userId, 0);
    setFocusedUserId(uid);

    const pos = getPositionByPercentage(p.percentage);

    if (pos && mapRef.current) {
      mapCardRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      mapRef.current.panTo(pos);
      mapRef.current.setZoom(Math.max(mapRef.current.getZoom() || 12, 14));

      setInfoWindow({
        position: pos,
        title: (p.name || `User ${uid}`).trim(),
        sub: `${formatPct(p.percentage)} • ${formatKm(p.distanceKm)}`,
      });
    }
  };

  const enriched = useMemo(() => {
    return participants.map((p) => ({
      ...p,
      name: (p.name || `User ${p.userId}`).trim(),
    }));
  }, [participants]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return enriched;

    return enriched.filter(
      (p) => p.name.toLowerCase().includes(q) || String(p.userId).includes(q)
    );
  }, [enriched, query]);

  const sorted = useMemo(() => {
    const dir = sortDir === "asc" ? 1 : -1;
    const arr = [...filtered];

    arr.sort((a, b) => {
      switch (sortKey) {
        case "name":
          return a.name.localeCompare(b.name) * dir;
        case "distance":
          return (safeNum(a.distanceKm) - safeNum(b.distanceKm)) * dir;
        case "progress":
        default:
          return (safeNum(a.percentage) - safeNum(b.percentage)) * dir;
      }
    });

    return arr;
  }, [filtered, sortDir, sortKey]);

  const stats = useMemo(() => {
    const total = participants.length;
    const avgPct = total
      ? participants.reduce((s, p) => s + safeNum(p.percentage, 0), 0) / total
      : 0;

    const finishersList = participants.filter((p) => safeNum(p.percentage, 0) >= 100);

    const getTimestamp = (p: any): number | null => {
      const keys = [
        "completedAt",
        "completed_at",
        "finishedAt",
        "finished_at",
        "updatedAt",
        "updated_at",
        "lastSeenAt",
        "last_seen_at",
      ];

      for (const key of keys) {
        const value = p?.[key];
        if (!value) continue;

        const t = Date.parse(String(value));
        if (Number.isFinite(t)) return t;
      }

      return null;
    };

    const winner = (() => {
      if (!finishersList.length) return null;

      const withTime = finishersList
        .map((p) => ({ p, t: getTimestamp(p) }))
        .filter((x) => x.t !== null) as { p: PortalParticipant; t: number }[];

      if (withTime.length) {
        withTime.sort((a, b) => a.t - b.t);
        return withTime[0].p;
      }

      return [...finishersList].sort(
        (a, b) => safeNum(b.distanceKm) - safeNum(a.distanceKm)
      )[0];
    })();

    return {
      total,
      avgPct,
      finishers: finishersList.length,
      winner,
    };
  }, [participants]);

  if (!productId) {
    return (
      <div className="portal-page">
        <div className="portal-card" style={{ padding: 16 }}>
          Missing productId in URL.
        </div>
      </div>
    );
  }

  const overlayBounds = parseBoundsLiteral((challenge as any)?.bounds);
  const challengeTitle = getChallengeName(challenge, productId);

  return (
    <div className="portal-page">
      <div className="portal-header">
        <div className="portal-title">
          <h1>{challengeTitle}</h1>
          <div className="portal-sub">
            Live participant overview • {sorted.length} shown
          </div>
        </div>

        <div className="portal-controls">
          <div style={{ position: "relative" }}>
            <Search
              size={16}
              style={{ position: "absolute", left: 10, top: 11, opacity: 0.6 }}
            />
            <input
              style={{ paddingLeft: 34 }}
              placeholder="Search name or userId"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>

          <select value={sortKey} onChange={(e) => setSortKey(e.target.value as SortKey)}>
            <option value="progress">Sort: progress</option>
            <option value="distance">Sort: distance</option>
            <option value="name">Sort: name</option>
          </select>

          <select value={sortDir} onChange={(e) => setSortDir(e.target.value as any)}>
            <option value="desc">Desc</option>
            <option value="asc">Asc</option>
          </select>

          <button onClick={loadAll} title="Refresh now">
            <RefreshCw size={16} style={{ verticalAlign: "-3px", marginRight: 6 }} />
            Refresh
          </button>
        </div>
      </div>

      <div className="portal-stats">
        <div className="portal-stat">
          <div className="label">Participants</div>
          <div className="value">{stats.total}</div>
        </div>

        <div className="portal-stat">
          <div className="label">Average progress</div>
          <div className="value">{formatPct(stats.avgPct)}</div>
        </div>

        <div className="portal-stat">
          <div className="label">Finishers</div>
          <div className="value">{stats.finishers}</div>
        </div>

        <div className="portal-stat">
          <div className="label">Winner</div>
          <div className="value" style={{ fontSize: 18 }}>
            {stats.winner ? stats.winner.name || `User ${stats.winner.userId}` : "–"}
          </div>
        </div>
      </div>

      {error && (
        <div
          className="portal-card"
          style={{
            padding: 12,
            marginBottom: 12,
            borderColor: "rgba(255,0,0,0.25)",
          }}
        >
          <b>Load error:</b> {error}
        </div>
      )}

      <div className="portal-grid">
        <div className="portal-card" ref={mapCardRef}>
          <div className="portal-card-header">
            <h2>Route map</h2>

            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <span className="portal-pill">{formatKm(totalDistanceKm)} total</span>

              <button onClick={fitToRouteOrBounds} title="Fit to bounds/route">
                <Target size={16} style={{ verticalAlign: "-3px", marginRight: 6 }} />
                Fit
              </button>
            </div>
          </div>

          <div style={{ height: 520 }}>
            {isLoaded ? (
              <GoogleMap
                mapContainerStyle={{ width: "100%", height: "100%" }}
                center={routePath[0] || { lat: 51.5072, lng: -0.1276 }}
                zoom={routePath.length ? 10 : 5}
                onLoad={(map) => {
                  mapRef.current = map;
                  window.setTimeout(() => fitToRouteOrBounds(), 250);
                }}
                options={{
                  mapTypeControl: false,
                  fullscreenControl: false,
                  streetViewControl: false,
                  clickableIcons: false,
                }}
              >
                {(challenge as any)?.mapOverlay && overlayBounds && (
                  <GroundOverlay
                    key="portal-overlay"
                    url={(challenge as any).mapOverlay}
                    bounds={overlayBounds as any}
                  />
                )}

                {!!routePath.length && (
                  <Polyline
                    path={routePath}
                    options={{
                      strokeOpacity: 0.85,
                      strokeWeight: 4,
                    }}
                  />
                )}

                {participants.map((p, index) => {
                  const pos = getPositionByPercentage(p.percentage);
                  if (!pos) return null;

                  const isFocused = focusedUserId === p.userId;

                  return (
                    <Marker
                      key={`${p.userId}-${p.name}-${p.percentage}-${index}`}
                      position={pos}
                      onClick={() =>
                        setInfoWindow({
                          position: pos,
                          title: (p.name || `User ${p.userId}`).trim(),
                          sub: `${formatPct(p.percentage)} • ${formatKm(p.distanceKm)}`,
                        })
                      }
                      options={{ zIndex: isFocused ? 999 : undefined }}
                    />
                  );
                })}

                {infoWindow && (
                  <InfoWindow position={infoWindow.position} onCloseClick={() => setInfoWindow(null)}>
                    <div style={{ maxWidth: 260 }}>
                      <div style={{ fontWeight: 700, marginBottom: 4 }}>{infoWindow.title}</div>
                      {infoWindow.sub ? <div className="portal-muted">{infoWindow.sub}</div> : null}
                    </div>
                  </InfoWindow>
                )}
              </GoogleMap>
            ) : (
              <div style={{ padding: 16 }}>{loading ? "Loading map…" : "Map not available."}</div>
            )}
          </div>
        </div>

        <div className="portal-card">
          <div className="portal-card-header">
            <h2>Participants</h2>
            <span className="portal-pill">{sorted.length} shown</span>
          </div>

          <div className="portal-table-wrap" style={{ maxHeight: 520 }}>
            <table className="portal-table">
              <thead>
                <tr>
                  <th>User</th>
                  <th>Progress</th>
                  <th></th>
                </tr>
              </thead>

              <tbody>
                {sorted.map((p, index) => {
                  const pct = Math.max(0, Math.min(100, safeNum(p.percentage, 0)));
                  const username = cleanUsername(p.name || "");
                  const profileUrl = `https://medalmad.com/members/${username}/`;

                  return (
                    <tr key={`${p.userId}-${p.name}-${index}`}>
                      <td>
                        <div style={{ fontWeight: 700 }}>
                          <a href={profileUrl} target="_blank" rel="noreferrer">
                            {p.name || `User ${p.userId}`}
                          </a>
                        </div>

                        <div className="portal-muted">
                          #{p.userId} • {formatKm(p.distanceKm)}
                        </div>
                      </td>

                      <td>
                        <div className="portal-progress">
                          <div className="portal-bar">
                            <div style={{ width: `${pct}%` }} />
                          </div>
                          <div>{formatPct(pct)}</div>
                        </div>
                      </td>

                      <td>
                        <div className="portal-row-actions">
                          <button onClick={() => focusParticipant(p)} title="Show on map">
                            Map
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}

                {!sorted.length && (
                  <tr>
                    <td colSpan={3} style={{ padding: 14 }}>
                      {loading ? "Loading…" : "No participants found."}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
